import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, BadgeCheck, BookOpen, Users, Send, Check, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { LaunchCountdown } from "./LaunchCountdown";

/**
 * PULA LEARN — bundle catalogue (PRE-LAUNCH mode).
 * CTA logic: pre-launch -> waitlist capture; post-accreditation -> enrol/checkout.
 * Accreditation state comes from the server (CPD_ACCREDITED env), never hardcoded.
 */

export default function LearnBundlesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", interest: "individual", bundle_slug: "" });
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(null);

  useEffect(() => {
    api.get("/learn/bundles")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load bundles — please refresh."))
      .finally(() => setLoading(false));
  }, []);

  const accredited = data?.cpd_accredited === true;
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.post("/learn/waitlist", {
        ...form, bundle_slug: form.bundle_slug || null,
      });
      setJoined(res);
      toast.success(res.already_on_list ? "You're already on the list." : `You're #${res.position} on the launch list.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <main className="bg-white" data-testid="learn-bundles-page">
      <section className="mv-green-bg text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Training Bundles</h1>
          <p className="text-base md:text-lg text-white/85 max-w-2xl">
            Launching with three bundles for UK care workers and providers.
            {accredited
              ? ` CPD Accredited — Provider No. ${data?.cpd_provider_number}.`
              : " Prices shown are launch prices — joining the waiting list today costs nothing."}
          </p>
          <p className="text-sm text-white/70 mt-3">
            Training a team? <a href="/learn/employer" className="underline mv-gold-text" data-testid="bundles-employer-link">Use the Employer Portal</a> to reserve seats and track progress.
          </p>
        </div>
      </section>

      <LaunchCountdown launchDate={data?.launch_date} accredited={accredited} />

      {!accredited && (
        <div className="border-b mv-hairline bg-amber-50" data-testid="bundles-prelaunch-banner">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex gap-3">
            <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-600" />
            <p className="text-sm text-amber-900">
              {data?.prelaunch_notice || "Awaiting CPD Standards Office accreditation — certificates cannot be issued yet."}
            </p>
          </div>
        </div>
      )}

      <section className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {loading ? (
            <p className="text-sm text-gray-500" data-testid="bundles-loading">Loading bundles…</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {(data?.bundles || []).map((b) => (
                <article key={b.slug} data-testid={`bundle-card-${b.slug}`}
                  className="rounded-xl border mv-hairline bg-white p-6 flex flex-col hover:shadow-lg transition-shadow">
                  <div className="mb-4">
                    {accredited ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-900">
                        <BadgeCheck size={13} /> CPD Accredited
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 border mv-hairline">
                        <Clock size={13} /> PRE-LAUNCH
                      </span>
                    )}
                  </div>
                  <h2 className="text-base md:text-lg font-bold mb-1">{b.title}</h2>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-3">
                    <Users size={13} /> {b.audience}
                  </p>
                  <p className="text-sm text-gray-600 mb-4 flex-1">{b.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-5">
                    <span className="flex items-center gap-1"><BookOpen size={13} /> {b.courses?.length || 1} course{(b.courses?.length || 1) > 1 ? "s" : ""}</span>
                    <span className="flex items-center gap-1"><Clock size={13} /> ~{b.cpd_hours_target} CPD hours (target)</span>
                  </div>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-2xl font-bold mv-green-text" data-testid={`bundle-price-${b.slug}`}>£{b.price_gbp?.toFixed(2)}</span>
                    {accredited ? (
                      <button data-testid={`bundle-enrol-${b.slug}`}
                        className="btn-green px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                        onClick={() => toast.info("Checkout opens shortly.")}>
                        Enrol now
                      </button>
                    ) : (
                      <button data-testid={`bundle-notify-${b.slug}`}
                        className="btn-gold px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                        onClick={() => {
                          setForm({ ...form, bundle_slug: b.slug });
                          document.getElementById("learn-waitlist-form")?.scrollIntoView({ behavior: "smooth" });
                        }}>
                        Notify me at launch
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Waitlist capture */}
          {!accredited && (
            <div id="learn-waitlist-form" className="mt-16 max-w-2xl mx-auto">
              {joined ? (
                <div className="rounded-xl border mv-hairline p-8 text-center bg-emerald-50" data-testid="waitlist-success">
                  <Check size={36} className="mx-auto mb-3 mv-green-text" />
                  <h2 className="text-lg font-bold mb-2">
                    {joined.already_on_list ? "You're already on the list" : `You're #${joined.position} on the launch list`}
                  </h2>
                  <p className="text-sm text-gray-600">{joined.message}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="rounded-xl border mv-hairline p-8 bg-white" data-testid="learn-waitlist-form-el">
                  <h2 className="text-lg font-bold mb-1">Join the launch waiting list</h2>
                  <p className="text-sm text-gray-600 mb-6">
                    No charge, no commitment. We'll email you the day accredited enrolment opens.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input data-testid="waitlist-name-input" value={form.name} onChange={on("name")}
                      placeholder="Full name" className="border mv-hairline rounded-lg px-3 py-2.5 text-sm w-full" />
                    <input data-testid="waitlist-email-input" type="email" value={form.email} onChange={on("email")}
                      placeholder="Email address" className="border mv-hairline rounded-lg px-3 py-2.5 text-sm w-full" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <select data-testid="waitlist-interest-select" value={form.interest} onChange={on("interest")}
                      className="border mv-hairline rounded-lg px-3 py-2.5 text-sm w-full bg-white">
                      <option value="individual">I'm an individual care worker</option>
                      <option value="employer">I'm an employer / provider</option>
                    </select>
                    <select data-testid="waitlist-bundle-select" value={form.bundle_slug} onChange={on("bundle_slug")}
                      className="border mv-hairline rounded-lg px-3 py-2.5 text-sm w-full bg-white">
                      <option value="">Any bundle</option>
                      {(data?.bundles || []).map((b) => (
                        <option key={b.slug} value={b.slug}>{b.title}</option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" disabled={busy} data-testid="waitlist-submit-btn"
                    className="btn-green inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60">
                    <Send size={16} /> {busy ? "Joining…" : "Join waiting list — free"}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
